import "./ClienteFormModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { UsuarioAppResponse } from "../../../shared/response/seguridad/UsuarioAppResponse";
import { UsuarioAppPayload } from "../../../shared/services/api/payload/seguridad/UsuarioAppPayload";
import { UsuarioAppSaveService } from "../../../shared/services/seguridad/UsuarioAppSaveService";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Misma regla que Services.Implementations.Seguridad.PasswordReset.PasswordPolicy (el servidor es la
// autoridad; esto solo evita un viaje al servidor para un error obvio).
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 100;
const PASSWORD_HAS_LETTER_RE = /[a-zA-Z]/;
const PASSWORD_HAS_DIGIT_RE = /[0-9]/;

export class UsuarioAppFormModal extends BaseSmallModalFormComponent<
  UsuarioAppResponse,
  UsuarioAppPayload,
  UsuarioAppSaveService
> {
  private _nombreInput!: HTMLInputElement;
  private _emailInput!: HTMLInputElement;
  private _passwordInput!: HTMLInputElement;
  private _nombreErrorSpan!: HTMLSpanElement;
  private _emailErrorSpan!: HTMLSpanElement;
  private _passwordErrorSpan!: HTMLSpanElement;

  constructor(
    private readonly _clienteId: string,
    private readonly _onSaved: (item: UsuarioAppResponse) => void,
    private readonly _item?: UsuarioAppResponse,
  ) {
    super(new UsuarioAppSaveService());
  }

  protected get title(): string {
    return this._item ? "Editar usuario" : "Nuevo usuario";
  }

  protected renderBody(): string {
    const nombre = this._item?.nombre ?? "";
    const email  = this._item?.email  ?? "";

    // Sin contraseña provista: al crear la genera el servidor y la manda por correo; al editar, no
    // se toca la actual. Con contraseña provista (campo opcional de abajo): se usa esa -al editar,
    // además, cierra las sesiones abiertas de la cuenta-.
    const hint = this._item
      ? "Cambiar el email cambia la credencial de ingreso de esta cuenta."
      : "Si dejás la contraseña vacía, se generará una y se enviará a este correo.";

    return `
      <div class="cl-form-fields">
        <div class="cl-form-group">
          <label class="cl-form-label" for="ua-f-nombre">
            Nombre <span class="cl-required">*</span>
          </label>
          <input
            id="ua-f-nombre"
            class="sx-input"
            type="text"
            placeholder="Nombre del usuario"
            value="${this._esc(nombre)}"
            maxlength="250"
          />
          <span class="sx-field-error" id="ua-f-nombre-error"></span>
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="ua-f-email">
            Email <span class="cl-required">*</span>
          </label>
          <input
            id="ua-f-email"
            class="sx-input"
            type="email"
            placeholder="usuario@empresa.com"
            value="${this._esc(email)}"
            maxlength="250"
          />
          <span class="cl-form-hint">${hint} Debe ser único en todo el sistema.</span>
          <span class="sx-field-error" id="ua-f-email-error"></span>
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="ua-f-password">
            Contraseña ${this._item ? "(dejar vacío para no cambiar)" : "(opcional)"}
          </label>
          <input
            id="ua-f-password"
            class="sx-input"
            type="password"
            autocomplete="new-password"
            placeholder="••••••••"
            maxlength="${PASSWORD_MAX}"
          />
          <span class="cl-form-hint">
            ${this._item
              ? "Si la llenás, se envían las credenciales nuevas por correo y se cierran las sesiones abiertas de esta cuenta."
              : "Si la llenás, se usa esa en vez de generar una, y también se envía por correo."}
            Mínimo ${PASSWORD_MIN} caracteres, con al menos una letra y un número.
          </span>
          <span class="sx-field-error" id="ua-f-password-error"></span>
        </div>
      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._nombreInput   = el.querySelector<HTMLInputElement>("#ua-f-nombre")!;
    this._emailInput    = el.querySelector<HTMLInputElement>("#ua-f-email")!;
    this._passwordInput = el.querySelector<HTMLInputElement>("#ua-f-password")!;
    this._nombreErrorSpan   = el.querySelector<HTMLSpanElement>("#ua-f-nombre-error")!;
    this._emailErrorSpan    = el.querySelector<HTMLSpanElement>("#ua-f-email-error")!;
    this._passwordErrorSpan = el.querySelector<HTMLSpanElement>("#ua-f-password-error")!;

    this._nombreInput.addEventListener("input", () => this._validate());
    this._emailInput.addEventListener("input", () => this._validate());
    this._passwordInput.addEventListener("input", () => this._validate());
    this._validate();
  }

  protected buildPayload(): UsuarioAppPayload {
    const password = this._passwordInput.value;
    return {
      id:        this._item?.id ?? null,
      clienteId: this._clienteId,
      nombre:    this._nombreInput.value.trim(),
      email:     this._emailInput.value.trim(),
      password:  password ? password : null,
    };
  }

  protected override onSaved(item: UsuarioAppResponse): void {
    this._onSaved(item);
    this.dispose();
  }

  // La unicidad del email es global y no se puede verificar acá: la valida el servidor (400).
  private _validate(): void {
    const nombreError = this._nombreInput.value.trim().length >= 3 ? null : "Mínimo 3 caracteres";
    const emailError = EMAIL_RE.test(this._emailInput.value.trim()) ? null : "Ingrese un email válido";
    const passwordError = this._getPasswordError(this._passwordInput.value);

    this._setFieldError(this._nombreInput, this._nombreErrorSpan, nombreError);
    this._setFieldError(this._emailInput, this._emailErrorSpan, emailError);
    this._setFieldError(this._passwordInput, this._passwordErrorSpan, passwordError);

    this.setSaveDisabled(!!(nombreError || emailError || passwordError));
  }

  /** Vacía es válida (campo opcional); llena, debe cumplir la misma regla que el servidor. */
  private _getPasswordError(password: string): string | null {
    if (!password) return null;
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      return `Debe tener entre ${PASSWORD_MIN} y ${PASSWORD_MAX} caracteres`;
    }
    if (!PASSWORD_HAS_LETTER_RE.test(password) || !PASSWORD_HAS_DIGIT_RE.test(password)) {
      return "Debe incluir al menos una letra y un número";
    }
    return null;
  }

  private _setFieldError(input: HTMLInputElement, span: HTMLSpanElement, message: string | null): void {
    span.textContent = message ?? "";
    input.classList.toggle("error", message !== null);
  }

  private _esc(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
